import { Component, Inject, OnDestroy } from "@angular/core";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatRadioChange } from "@angular/material/radio";
import { MatSnackBar } from "@angular/material/snack-bar";
import { FileLogger } from "@comunicare/comunicare-helper";
import { DISABLE_REASON, IPatientUser } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import * as moment from "moment";
import { forkJoin, of, Subject } from "rxjs";
import { catchError, defaultIfEmpty, map, takeUntil } from "rxjs/operators";
import { IPatientDeactivation } from "src/app/models/patient-deactivation.model";
import { PatientService } from "src/app/providers/patient.service";
import { SessionService } from "src/app/providers/session.service";

@Component({
  selector: "app-patient-disable-dialog",
  templateUrl: "./patient-disable-dialog.component.html",
  styleUrls: ["./patient-disable-dialog.component.scss"],
})
export class PatientDisableDialogComponent implements OnDestroy {
  private onDestroy$ = new Subject<void>();
  public selectedReason: DISABLE_REASON;
  public otherReason = "";
  public reasons = DISABLE_REASON;
  /** false: the patient is expected to come back. true: he will not. */
  public isPermanent = false;
  public startDate: moment.Moment = null;
  public endDate: moment.Moment = null;
  /** A deactivation cannot start in the future. */
  public maxStartDate = moment();
  /** The patient cannot come back before the day after the deactivation started. */
  public minEndDate = moment().add(1, "day");
  /** Whether the practitioner already tried to validate once - the missing reason error only shows up from then on. */
  private attemptedSubmit = false;
  /** Whether the deactivations are being saved - validating again meanwhile would send them twice. */
  public isSaving = false;

  constructor(
    protected patientService: PatientService,
    private sessionService: SessionService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService,
    private dialogRef: MatDialogRef<PatientDisableDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: { patients: IPatientUser[] }
  ) {}

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public get canSubmit(): boolean {
    if (!this.selectedReason) {
      return false;
    }
    if (this.startDate && (!this.startDate.isValid() || this.startDate.isAfter(moment(), "day"))) {
      return false;
    }
    if (!this.isPermanent && this.endDate && (!this.endDate.isValid() || this.endDate.isBefore(this.minEndDate, "day"))) {
      return false;
    }
    return true;
  }

  /** Shown once the practitioner tried to validate the "other" reason while leaving it blank. */
  public get otherReasonMissing(): boolean {
    return this.attemptedSubmit && this.selectedReason === DISABLE_REASON.OTHER && !this.otherReason;
  }

  /**
   * Every reason but OTHER, which the template renders apart because of its free text field.
   * DEATH only shows up once the deactivation is set to permanent. Driven off Object.values() so a
   * reason added to the enum tomorrow shows up here on its own, with no template/component change.
   */
  public get simpleReasons(): DISABLE_REASON[] {
    return Object.values(DISABLE_REASON).filter(
      (reason) => reason !== DISABLE_REASON.OTHER && (reason !== DISABLE_REASON.DEATH || this.isPermanent)
    );
  }

  public onTypeChange($event: MatRadioChange | Event): void {
    if (!this.isRadioChange($event)) {
      return;
    }
    this.isPermanent = $event.value;
    if (this.isPermanent) {
      this.endDate = null;
    } else if (this.selectedReason === DISABLE_REASON.DEATH) {
      // DEATH only makes sense for a permanent deactivation.
      this.selectedReason = undefined;
    }
  }

  public onRadioChange($event: MatRadioChange | Event): void {
    if (!this.isRadioChange($event)) {
      return;
    }
    this.selectedReason = $event.value;
    if (this.selectedReason !== DISABLE_REASON.OTHER) {
      // Drop what was typed for "other" so it cannot be sent along another reason.
      this.otherReason = "";
    }
  }

  /**
   * A (change) binding on mat-radio-group fires on the group output but also on the native change
   * events bubbling up from the controls nested in the group - the free text input of the "other"
   * reason emits one every time it is edited then left. Those carry no value and must be ignored,
   * otherwise leaving the free text field clears the reason the practitioner just picked.
   */
  private isRadioChange($event: MatRadioChange | Event): $event is MatRadioChange {
    return $event instanceof MatRadioChange;
  }

  public onStartDateChange(): void {
    this.minEndDate = (this.startDate?.isValid() ? this.startDate.clone() : moment()).add(1, "day");
    if (this.endDate?.isBefore(this.minEndDate, "day")) {
      this.endDate = null;
    }
  }

  public onYes(): void {
    if (this.isSaving) {
      return;
    }
    this.attemptedSubmit = true;
    if (this.otherReasonMissing) {
      return;
    }
    const deactivation = this.buildDeactivation();
    this.isSaving = true;
    // Each patient fails on its own: one failure must not cancel the deactivation of the others.
    const disables = this.data.patients.map((patientUser) =>
      this.patientService.disable(patientUser.patient, deactivation).pipe(
        map(() => true),
        catchError((err) => {
          FileLogger.error("PatientDisableDialogComponent", "onYes", err);
          return of(false);
        })
      )
    );
    // The dialog only closes once every answer came back: closing it destroys the component, which
    // would cancel the requests still running and skip the refreshes below.
    forkJoin(disables)
      .pipe(defaultIfEmpty([] as boolean[]), takeUntil(this.onDestroy$))
      .subscribe((results) => {
        this.isSaving = false;
        const succeeded = results.filter((ok) => ok).length;
        const failed = results.length - succeeded;
        const msg = this.translateService.instant(failed ? "common.updateFail" : "common.success");
        this.snackBar.open(msg, "ok", { duration: 3000 });
        if (failed && !succeeded) {
          // Nothing was saved: stay open so the practitioner can try again.
          return;
        }
        if (succeeded) {
          this.sessionService.needRefreshPatientUser();
          this.sessionService.needRefreshPrescriptionsTile();
        }
        this.dialogRef.close(deactivation);
      });
  }

  private buildDeactivation(): IPatientDeactivation {
    return {
      // What the practitioner typed for "other" travels apart from the reason.
      reason: this.selectedReason,
      reasonDetail: this.selectedReason === DISABLE_REASON.OTHER && this.otherReason ? this.otherReason : undefined,
      permanent: this.isPermanent,
      date: this.startDate?.isValid() ? this.startDate.format() : undefined,
      predictedEndDate: !this.isPermanent && this.endDate?.isValid() ? this.endDate.format() : undefined,
    };
  }
}
